import dayjs from 'dayjs';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconEdit } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import CustomModal from '@/components/CustomModal';
import DatePicker from '@/components/DatePicker';
import Input from '@/components/Input/Input';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import NotificationDetailsSkeleton from '@/components/SkeletonLoader/NotificationDetailsSkeleton';
import TextArea from '@/components/TextArea';
import TimePicker from '@/components/TimePicker';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addNotificationSchema,
  TAddNotificationFormValues,
} from '@/schema/addNotificationSchema';
import {
  createNotification,
  updateNotification,
  getNotificationById,
} from '@/service/notificationServices';
import useCommonStore from '@/store/useCommonStore';
import { IAddNotificationPayload } from '@/types/payload';
import { getTodayIso } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

export type TDrawerMode = 'add' | 'view' | 'edit';

export interface INotificationData {
  id?: string;
  title: string;
  description: string;
  targeted_user: string;
  type: 'push' | 'email';
  is_scheduled: boolean;
  date?: string;
  time?: string;
}

interface IViewAddEditDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  mode: TDrawerMode;
  initialData?: INotificationData | null;
  onEditClick?: () => void;
  refetch?: () => void;
}

const TARGETED_USER_OPTIONS = [
  { value: 'parent', label: 'notification.form.targeted-user-options.parent' },
  {
    value: 'teacher',
    label: 'notification.form.targeted-user-options.teacher',
  },
  { value: 'school', label: 'notification.form.targeted-user-options.school' },
];

const SCHEDULE_OPTIONS = [
  { value: 'false', label: 'notification.form.publish-now' },
  { value: 'true', label: 'notification.form.schedule-later' },
];

const TYPE_OPTIONS = [
  { value: 'push', label: 'notification.form.push' },
  { value: 'email', label: 'notification.form.email' },
];

const EMPTY_DEFAULTS: TAddNotificationFormValues = {
  title: '',
  description: '',
  targeted_user: '',
  type: 'push',
  is_scheduled: false,
  date: '',
  time: '',
};

const ViewAddEditDrawer: React.FC<IViewAddEditDrawerProps> = ({
  isOpen,
  setIsOpen,
  mode,
  initialData,
  onEditClick,
  refetch,
}) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const isViewMode = mode === 'view';
  const isAddMode = mode === 'add';
  const [submitting, setSubmitting] = useState(false);
  const { loadingModal, setLoadingModal } = useCommonStore();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<TAddNotificationFormValues>({
    resolver: zodResolver(addNotificationSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  // Fetch data by ID if mode is view or edit
  useEffect(() => {
    const fetchNotificationDetails = async () => {
      if (!isOpen || !initialData?.id || (mode !== 'view' && mode !== 'edit'))
        return;

      try {
        setLoadingModal(true);
        const response = await getNotificationById(initialData.id);
        if (response?.status && response?.data) {
          const data = response.data;
          reset({
            title: data.title ?? '',
            description: data.description ?? '',
            targeted_user: data.targeted_user ?? '',
            type: data.type ?? 'push',
            is_scheduled: data.is_scheduled ?? false,
            date: data.date ?? '',
            time: data.time ? data.time.substring(0, 5) : '',
          });
        }
      } catch (error) {
        console.error('Error fetching notification details:', error);
      } finally {
        setLoadingModal(false);
      }
    };

    if (isOpen) {
      if (mode === 'add') {
        reset(EMPTY_DEFAULTS);
      } else {
        fetchNotificationDetails();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initialData?.id, reset]);

  const isScheduled = watch('is_scheduled');
  const selectedDate = watch('date');

  const minTime = useMemo(() => {
    if (!selectedDate) return undefined;
    const today = dayjs();
    if (selectedDate === today.format('YYYY-MM-DD')) {
      return today.format('HH:mm');
    }
    return undefined;
  }, [selectedDate]);

  const onSubmit = useCallback(
    async (data: TAddNotificationFormValues) => {
      setSubmitting(true);
      try {
        const payload: IAddNotificationPayload = {
          title: data.title,
          description: data.description,
          targeted_user: data.targeted_user,
          type: data.type,
          is_scheduled: data.is_scheduled,
          ...(data.is_scheduled && { date: data?.date }),
          ...(data.is_scheduled && { time: data?.time }),
        };

        let response;
        if (mode === 'add') {
          response = await createNotification(payload);
        } else if (mode === 'edit' && initialData?.id) {
          response = await updateNotification(initialData.id, payload);
        }

        if (response?.status) {
          reset();
          setIsOpen(false);
          refetch?.();
        }
      } catch (error) {
        console.error('Error submitting notification:', error);
      } finally {
        setSubmitting(false);
      }
    },
    [mode, initialData?.id, reset, setIsOpen, refetch]
  );

  const handleClose = useCallback(() => {
    reset();
    setIsOpen(false);
  }, [reset, setIsOpen]);

  // Header content with edit icon for view mode
  const headerContent = useMemo(() => {
    const heading = isAddMode
      ? formatText('notification.form.title')
      : formatText('notification.form.notification-details');

    return (
      <div className="flex items-center w-full justify-between gap-2">
        <p className="text-neutral-primary font-medium leading-7 text-lg">
          {heading}
        </p>
        {isViewMode && onEditClick && (
          <Button
            variant="link"
            onClick={onEditClick}
            className="p-1.5 rounded-sm bg-neutral-light-50 hover:bg-white text-neutral-pressed cursor-pointer transition-colors me-2"
            icon={<IconEdit size={20} color="#7F8383" />}
          />
        )}
      </div>
    );
  }, [isAddMode, isViewMode, formatText, onEditClick]);

  // Footer button config based on mode
  const confirmLabel = isAddMode
    ? formatText('notification.actions.add-notification')
    : formatText('notification.actions.save');

  if (!isOpen) return null;

  if (!hasAccess(ERoleAccess.NOTIFICATIONS)) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      headercontent={headerContent}
      onClose={handleClose}
      isFooter={!isViewMode}
      position="right"
      loadingModal={submitting}
      cancelButtonProps={{
        label: formatText('common.actions.cancel'),
        variant: 'link',
        onClick: handleClose,
        disabled: submitting,
      }}
      confirmButtonProps={
        isViewMode
          ? undefined
          : {
              label: confirmLabel,
              onClick: handleSubmit(onSubmit),
              loading: submitting,
              disabled: submitting,
            }
      }
    >
      {loadingModal ? (
        <NotificationDetailsSkeleton />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5 w-full"
        >
          {/* Details Section */}
          <p className="text-lg font-semibold text-neutral-primary">
            {formatText('notification.form.details')}
          </p>

          {/* Title */}
          <Input
            name="title"
            label={formatText('notification.form.notification-title')}
            placeholder={formatText('notification.form.title-placeholder')}
            register={register}
            disabled={isViewMode}
            error={
              errors.title?.message
                ? formatText(errors.title.message)
                : undefined
            }
            isRequired={!isViewMode}
          />

          {/* Description */}
          <TextArea
            name="description"
            label={formatText('notification.form.description')}
            placeholder={formatText(
              'notification.form.description-placeholder'
            )}
            register={register}
            maxLength={250}
            showCount={!isViewMode}
            disabled={isViewMode}
            error={
              errors.description?.message
                ? formatText(errors.description.message)
                : undefined
            }
            isRequired={!isViewMode}
          />

          {/* Targeted Users */}
          <Controller
            name="targeted_user"
            control={control}
            render={({ field, fieldState }) => (
              <Select
                options={TARGETED_USER_OPTIONS}
                name={formatText('notification.form.targeted-users')}
                placeholder={formatText(
                  'notification.form.targeted-users-placeholder'
                )}
                selected={field.value}
                onChange={(val) => field.onChange(val as string)}
                disabled={isViewMode}
                isRequired={!isViewMode}
                isTextFormat
                error={
                  fieldState.error?.message
                    ? formatText(fieldState.error.message)
                    : undefined
                }
              />
            )}
          />

          <p className="text-lg font-semibold text-neutral-primary">
            {formatText('notification.form.select-one')}
          </p>

          {/* Publish Now / Schedule for Later */}
          <Controller
            name="is_scheduled"
            control={control}
            render={({ field }) => (
              <RadioButtonGroup
                name="is_scheduled"
                options={SCHEDULE_OPTIONS}
                value={String(field.value)}
                onChange={(val: string) => field.onChange(val === 'true')}
                formatLabel={(label) => formatText(label)}
                disabled={isViewMode}
              />
            )}
          />

          <p className="text-lg font-semibold text-neutral-primary">
            {formatText('notification.form.notification-type')}
          </p>

          {/* Notification Type */}
          <Controller
            name="type"
            control={control}
            render={({ field, fieldState }) => (
              <RadioButtonGroup
                name="type"
                options={TYPE_OPTIONS}
                value={field.value}
                onChange={(val: string) => field.onChange(val)}
                formatLabel={(label) => formatText(label)}
                disabled={isViewMode}
                error={
                  fieldState.error?.message
                    ? formatText(fieldState.error.message)
                    : undefined
                }
              />
            )}
          />

          {/* Conditional Date & Time for Scheduled */}
          {isScheduled && (
            <div className="flex gap-4">
              <div className="flex-1">
                <Controller
                  name="date"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label={formatText('notification.form.date')}
                      placeholder={formatText(
                        'notification.form.date-placeholder'
                      )}
                      value={field.value ?? null}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      isRequired={!isViewMode}
                      min={getTodayIso()}
                      error={
                        fieldState.error?.message
                          ? formatText(fieldState.error.message)
                          : undefined
                      }
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Controller
                  name="time"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TimePicker
                      label={formatText('notification.form.time')}
                      placeholder={formatText(
                        'notification.form.time-placeholder'
                      )}
                      value={field.value ?? null}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      isRequired={!isViewMode}
                      minTime={minTime}
                      error={
                        fieldState.error?.message
                          ? formatText(fieldState.error.message)
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            </div>
          )}
        </form>
      )}
    </CustomModal>
  );
};

export default ViewAddEditDrawer;
